/**
 * D221 G4 (#1163) — the one rule for a text link or a bare text button that
 * leads somewhere (Back, cancel, skip, a link in a sentence): the hit area
 * is at least `--nw-size-tap` (44px) in both directions, and the DRAWN text
 * does not move.
 *
 * - `minHeight`/`minWidth` floor the box at the tap token; `inline-flex` +
 *   `alignItems: center` keeps the label vertically centred in it.
 * - `marginBlock` pulls the margin box back to one line (`1lh`, the
 *   element's own line height), so the layout footprint of the link is what
 *   it was before and the hit area grows symmetrically into the space above
 *   and below. The text stays where it was drawn.
 * - Width grows to the right of a left-aligned label (`justifyContent:
 *   flex-start`), and symmetrically for a centred one.
 *
 * Placement trap: `.nw-page-stack`, `.nw-stack-*` and friends space siblings
 * with `margin-top` on every child after the first. This style's
 * `marginBlock` is an inline style, so it WINS over that rule for a link
 * that is not the first child, and the stack's gap disappears. A caller in
 * such a stack, past the first child, adds the gap back in its own
 * `marginTop` (`calc(var(--nw-space-stack) + (1lh - var(--nw-size-tap)) /
 * 2)`). A block-level caller sets `display: "flex"` and `width:
 * "fit-content"` so the box sits in the flow like the old `display: block`
 * button.
 *
 * Only the box changes; font, colour, underline and position stay each
 * caller's own. Exported as a style object so a caller that cannot use
 * `SecondaryAction variant="link"` or `TextLink` can still spread it.
 */
/**
 * The half-leading that pulls the margin box back to one line: `(line - 44) /
 * 2`, negative. `1lh` (the element's own line height) is exact, but the
 * production browserslist still reaches browsers without the unit (iOS Safari
 * before 16.4, Chrome before 109, UC 15.5), where a `1lh` declaration fails
 * at computed-value time and the margin falls to 0, so every label would drop
 * 4 to 14px. Those get the same arithmetic from `em`: `1.5em` stands for the
 * line (`--nw-tap-lh`, unitless, defaults to the app's 1.5 body line height; a
 * caller with another line height sets the variable). The choice is made once,
 * at load, with `CSS.supports`; where `CSS` is absent (jsdom) the fallback
 * is chosen.
 */
export const SUPPORTS_LH = typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("margin-top", "1lh");
export const tapLine = (supported = SUPPORTS_LH) => (supported ? "1lh" : "(var(--nw-tap-lh, 1.5) * 1em)");
export const tapHalfLead = (supported = SUPPORTS_LH) => `((${tapLine(supported)} - var(--nw-size-tap)) / 2)`;
export const tapLinkStyle = (supported = SUPPORTS_LH) => ({
  display: "inline-flex",
  alignItems: "center",
  minHeight: "var(--nw-size-tap)",
  minWidth: "var(--nw-size-tap)",
  // A flex item with a `min-width` loses its `min-width: auto`, so without
  // this a link in a justify-between row shrinks below its own label.
  flexShrink: 0,
  marginBlock: `calc${tapHalfLead(supported)}`,
});
export const TAP_LINK_STYLE = Object.freeze(tapLinkStyle());

/**
 * The same floor for a link INSIDE A SENTENCE. An `inline-flex` box would
 * not wrap with the words around it (the whole link jumps to the next line
 * and the paragraph reflows), so a sentence link stays `inline` and grows
 * its hit area with vertical padding instead: padding on an inline box
 * paints and hit-tests but does not change the line box, so no text moves.
 * One third of the tap token each side, plus the line's own content area
 * (about 17px at the app's smallest link size), clears 44.
 */
export const TAP_INLINE_LINK_STYLE = Object.freeze({
  display: "inline",
  paddingBlock: "calc(var(--nw-size-tap) / 3)",
});
